import { Link, useLocation } from 'react-router-dom';
import { useEffect } from 'react';
import {
  ArrowRight,
  MapPin,
  Award,
  Megaphone,
  Users,
  ExternalLink,
  ScrollText,
  Lightbulb,
  MonitorPlay,
  MonitorSmartphone,
  Camera,
  ParkingMeter,
  Siren,
  Wrench,
  LifeBuoy,
} from 'lucide-react';
import './CompanyInfo.css';

const CompanyInfo = () => {
  const location = useLocation();

  useEffect(() => {
    if (!location.hash) return;

    const id = location.hash.replace('#', '');

    const timer = setTimeout(() => {
      const el = document.getElementById(id);

      if (el) {
        el.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }
    }, 80);

    return () => clearTimeout(timer);
  }, [location]);

  const companyData = [
    { label: '会社名', value: '株式会社 エイチエス' },
    { label: '代表者', value: '代表取締役　金 永埴' },
    { label: '創業', value: '2018年3月5日' },
    {
      label: '所在地',
      value: (
        <div className="address-block">
          <div className="address-item">
            <span className="address-tag">【川口工場・営業所】</span>
            <span>〒334-0061 埼玉県川口市新堀835-2</span>
          </div>
        </div>
      ),
    },
    {
      label: 'TEL',
      value: (
        <a href="tel:0368250852" className="contact-link">
          03-6825-0852
        </a>
      ),
    },
    { label: 'FAX', value: '050-6868-2670' },
    {
      label: 'E-mail',
      value: (
        <a href="mailto:kim@hs-i.co.jp" className="contact-link">
          kim@hs-i.co.jp
        </a>
      ),
    },
    {
      label: '営業時間',
      value: (
        <div className="hours-block">
          <div>平日 9:00〜18:00</div>
          <div className="hours-note">※現場対応・施工日程についてはご相談ください。</div>
        </div>
      ),
    },
    {
      label: '対応エリア',
      value: (
        <div className="hours-block">
          <div>東京都・埼玉県・千葉県を中心に、関東エリアのご相談に対応しています。</div>
          <div className="hours-note">※内容によりその他地域もご相談可能です。</div>
        </div>
      ),
    },
  ];

  const businessAreas = [
    { text: '看板・サインの製作・施工', Icon: ScrollText },
    { text: 'LED照明の選定・設置', Icon: Lightbulb },
    { text: 'LEDビジョンの導入・施工', Icon: MonitorPlay },
    { text: '電光掲示板の設置・施工', Icon: MonitorSmartphone },
    { text: '防犯カメラシステムの構築', Icon: Camera },
    { text: 'パーキングシステムの設置', Icon: ParkingMeter },
    { text: '緊急出動警報板の製作・設置', Icon: Siren },
    { text: 'カスタム金物の製作', Icon: Wrench },
    { text: '施工後のメンテナンス・サポート', Icon: LifeBuoy },
  ];

  const constructionLicense = [
    { label: '建設業許可番号', value: '東京都知事　許可（般-5）第157570号' },
    { label: '建設業許可の種類', value: '鋼構造物工事業' },
  ];

  const outdoorAdRegistrations = [
    {
      prefecture: '東京都',
      items: [
        '東京都屋外広告業登録　都広（0）第4552号',
        '八王子市特例屋外広告業届出　第588号',
      ],
    },
    {
      prefecture: '埼玉県',
      items: [
        '埼玉県屋外広告業登録　埼広（01）第2382号',
        'さいたま市特例屋外広告業　第1001号',
        '川越市特例屋外広告業　第961号',
        '越谷市特例屋外広告業　第0755号',
        '川口市特例屋外広告業　第536号',
      ],
    },
    {
      prefecture: '千葉県',
      items: [
        '千葉県屋外広告業登録　第212256号',
        '千葉市屋外広告業登録　第23076号',
        '船橋市特例屋外広告業　第863号',
        '柏市特例屋外広告業　第740号',
      ],
    },
    {
      prefecture: '神奈川県',
      items: [
        '神奈川県屋外広告業登録　第2520号',
        '横浜市特例屋外広告業届出　第2750号',
        '川崎市特例屋外広告業届出　第1357号',
        '横須賀市特例屋外広告業届出　第1036号',
        '相模原市特例屋外広告業届出　第1191号',
      ],
    },
    {
      prefecture: '群馬県',
      items: [
        '群馬県屋外広告業登録制度（1）第1357号',
        '前橋市屋外広告業登録　前広（1）第650号',
        '高崎市屋外広告業登録　高広（1）第486号',
      ],
    },
    {
      prefecture: '茨城県',
      items: ['茨城県屋外広告業登録　5-0001628号'],
    },
    {
      prefecture: '栃木県',
      items: [
        '栃木県屋外広告業登録　第001305号',
        '宇都宮市屋外広告業登録　第00918号',
      ],
    },
  ];

  const memberships = [
    '一般社団法人 LED光源普及開発機構',
    'ライオンズクラブ国際協会',
    '東京都商工会連合',
    '川口商工会議所',
  ];

  const qualificationGroups = [
    {
      title: '看板・高所作業関連',
      items: [
        '屋外広告物点検技能講習修了',
        '高所作業車運転技能講習修了',
        'フルハーネス型墜落制止用器具特別教育修了',
      ],
    },
    {
      title: '重機・加工関連',
      items: [
        '小型移動式クレーン運転技能講習修了',
        '玉掛け技能講習修了',
        'アーク溶接等特別教育修了',
        '自由研削といし特別教育修了',
        '振動工具取扱教育修了',
        '丸のこ等取扱教育修了',
      ],
    },
    {
      title: '安全衛生管理関連',
      items: [
        '職長・安全衛生責任者教育修了',
        '安全衛生推進者養成講習修了',
        '低圧電気取扱特別教育修了',
      ],
    },
    {
      title: 'その他の技能・認定',
      items: [
        '無人航空機操縦技能認定（民間認定）',
        '上級救命技能認定',
      ],
    },
  ];

  return (
    <div className="company-info-page ci-refined" data-testid="company-info-page">
      <section className="page-hero ci-hero" data-testid="ci-hero">
        <div className="ci-hero-bg" aria-hidden="true">
          <div className="ci-hero-grid" />
          <div className="ci-hero-skyline" />
        </div>

        <div className="container ci-hero-inner">
          <span className="ci-hero-eyebrow">COMPANY</span>
          <h1>会社概要</h1>
          <span className="ci-hero-divider" aria-hidden="true" />
          <p>株式会社エイチエスの会社情報、事業内容、許可・登録、アクセスについてご案内します。</p>
        </div>
      </section>

      <section className="section" id="company-overview">
        <div className="container">
          <div className="ci-section-head">
            <span className="ci-eyebrow">COMPANY PROFILE</span>
            <h2 className="section-title-left">会社情報</h2>
          </div>

          <div className="company-table" data-testid="company-table">
            <table>
              <tbody>
                {companyData.map((row, index) => (
                  <tr key={index}>
                    <th>{row.label}</th>
                    <td>{row.value}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section className="section ci-message-section" id="message">
        <div className="container">
          <div className="ci-section-head">
            <span className="ci-eyebrow">TOP MESSAGE</span>
            <h2 className="section-title-left">代表メッセージ</h2>
          </div>

          <div className="ci-message-grid" data-testid="representative-message">
            <div className="ci-message-body">
              <p className="ci-message-para">
                株式会社エイチエスでは、看板・LED照明・防犯カメラ・LEDビジョン・電光掲示板など、現場に必要な設備を取り扱っております。
              </p>
              <p className="ci-message-para">
                製品を販売するだけでなく、設置場所や使用目的を丁寧にお伺いし、現場に合った形でご提案することを大切にしています。
              </p>
              <p className="ci-message-para">
                設置後も安心してお使いいただけるよう、ご相談をいただきながら丁寧に対応してまいります。
              </p>
              <p className="ci-message-para">
                今後とも、株式会社エイチエスをよろしくお願い申し上げます。
              </p>

              <div className="ci-message-signature">
                <span className="ci-message-signature-line" aria-hidden="true" />
                <div className="ci-message-signature-text">
                  <span className="ci-message-role">代表取締役</span>
                  <span className="ci-message-name">金 永埴</span>
                </div>
              </div>
            </div>

            <div className="ci-message-photo" data-testid="representative-photo">
              <img
                src="/images/representative.png"
                alt="代表取締役　金 永埴"
                className="ci-message-photo-img"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="section gray-section" id="business">
        <div className="container">
          <div className="ci-section-head">
            <span className="ci-eyebrow">OUR BUSINESS</span>
            <h2 className="section-title-left">事業内容</h2>
          </div>

          <div className="business-list">
            {businessAreas.map((item, index) => {
              const Icon = item.Icon;

              return (
                <div key={index} className="business-item" data-testid={`business-item-${index}`}>
                  <span className="business-icon">
                    <Icon size={20} strokeWidth={1.6} />
                  </span>
                  <span className="business-text">{item.text}</span>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="section" id="licenses">
        <div className="container">
          <div className="ci-section-head">
            <span className="ci-eyebrow">LICENSES & REGISTRATIONS</span>
            <h2 className="section-title-left">許可・登録</h2>
            <p className="ci-section-intro" data-testid="license-intro">
              建設業許可および各自治体の屋外広告業登録に基づき、看板・サイン関連工事に対応しています。
            </p>
          </div>

          <div className="license-group" data-testid="license-construction">
            <div className="license-group-head">
              <span className="license-group-icon">
                <Award size={22} strokeWidth={1.6} />
              </span>
              <h3 className="license-group-title">建設業許可</h3>
            </div>

            <div className="license-card">
              <dl className="license-defs">
                {constructionLicense.map((row, i) => (
                  <div key={i} className="license-defs-row">
                    <dt>{row.label}</dt>
                    <dd>{row.value}</dd>
                  </div>
                ))}
              </dl>
            </div>
          </div>

          <div className="license-group" data-testid="license-outdoor-ad">
            <div className="license-group-head">
              <span className="license-group-icon">
                <Megaphone size={22} strokeWidth={1.6} />
              </span>
              <h3 className="license-group-title">屋外広告業登録</h3>
            </div>

            <div className="prefecture-grid">
              {outdoorAdRegistrations.map((pref, i) => (
                <div key={i} className="prefecture-card" data-testid={`prefecture-${i}`}>
                  <div className="prefecture-name">{pref.prefecture}</div>
                  <ul className="prefecture-list">
                    {pref.items.map((item, j) => (
                      <li key={j}>{item}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>

          <div className="license-group license-group-last" id="memberships" data-testid="license-memberships">
            <div className="license-group-head">
              <span className="license-group-icon">
                <Users size={22} strokeWidth={1.6} />
              </span>
              <h3 className="license-group-title">加盟団体</h3>
            </div>

            <div className="membership-list">
              {memberships.map((m, i) => (
                <div key={i} className="membership-item">
                  <span className="membership-bullet" />
                  <span>{m}</span>
                </div>
              ))}
            </div>
          </div>


        </div>
      </section>

      <section className="section gray-section ci-qualification-section" id="qualifications" data-testid="qualifications-section">
        <div className="container">
      <div className="ci-qualifications" data-testid="qualifications">
        <div className="ci-qualification-heading">
          <span className="ci-eyebrow">QUALIFICATIONS &amp; SAFETY TRAINING</span>
          <h2 className="section-title-left ci-qualification-title">保有資格・技能講習・安全衛生教育</h2>
          <p className="ci-qualification-intro">
            当社では、安全管理の徹底と施工品質の向上に取り組み、
            各種技能講習・特別教育・安全衛生教育を修了した人員が在籍しています。
          </p>
        </div>

        <div className="ci-qualification-grid">
          {qualificationGroups.map(({ title, items }) => (
            <div className="ci-qualification-card" key={title}>
              <h4 className="ci-qualification-card-title">{title}</h4>
              <ul className="ci-qualification-list">
                {items.map((item) => <li key={item}>{item}</li>)}
              </ul>
            </div>
          ))}
        </div>
      </div>
        </div>
      </section>

      <section className="section gray-section access-section-final" id="access">
        <div className="container">
          <div className="ci-section-head">
            <span className="ci-eyebrow">ACCESS</span>
            <h2 className="section-title-left">アクセス</h2>
          </div>

          <p className="access-intro" data-testid="access-intro">
            製品確認・お打ち合わせ等は、川口工場・営業所にて対応しております。
            <br />
            ご来社の際は、事前にお問い合わせください。
          </p>

          <div className="access-final-card" data-testid="access-kawaguchi-card">
            <div className="access-final-info">
              <div className="access-card-head">
                <span className="access-card-icon">
                  <MapPin size={20} strokeWidth={1.7} />
                </span>
                <span className="access-card-branch">川口工場・営業所</span>
              </div>

              <p className="access-card-company">株式会社 エイチエス</p>
              <p className="access-card-line">〒334-0061</p>
              <p className="access-card-line">埼玉県川口市新堀835-2</p>

              <a
                className="access-map-btn"
                href="https://www.google.com/maps/search/?api=1&query=%E5%9F%BC%E7%8E%89%E7%9C%8C%E5%B7%9D%E5%8F%A3%E5%B8%82%E6%96%B0%E5%A0%80835-2"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="access-map-button"
              >
                Google Mapで見る
                <ExternalLink size={16} strokeWidth={1.8} />
              </a>
            </div>

            <div className="access-final-map" data-testid="access-map-embed">
              <iframe
                title="川口工場・営業所の地図"
                src="https://www.google.com/maps?q=%E5%9F%BC%E7%8E%89%E7%9C%8C%E5%B7%9D%E5%8F%A3%E5%B8%82%E6%96%B0%E5%A0%80835-2&output=embed"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
              />
            </div>
          </div>
        </div>
      </section>

      <section className="ci-cta-section" data-testid="ci-cta-section">
        <div className="container">
          <div className="ci-cta-inner">
            <span className="ci-cta-eyebrow">CONTACT</span>
            <h2 className="ci-cta-title">製品選びや施工のご相談は、お気軽にお問い合わせください</h2>
            <p className="ci-cta-body">
              現場の状況やご希望を確認し、必要な内容を分かりやすくご案内いたします。
            </p>
            <Link to="/contact" className="ci-cta-btn" data-testid="ci-cta-btn">
              お問い合わせフォームへ
              <ArrowRight size={18} strokeWidth={2} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CompanyInfo;